import {
  getDayAvailabilityForDate,
  toISODateString,
} from "../../../utils/availability";
import { cloudinaryImageUrl, IMAGE_WIDTH } from "../../../utils/cloudinaryDelivery";

// The header section of the booking page: the picture, the heading, the
// tagline, the three chips and the bio (or, on a Custom page with the images
// header, the pro's header images and the bio). The bio stays inside .profile-hero on
// purpose: only .profile-hero, .profile-gallery and .services-list carry the
// page's side padding (ProfessionalProfile.css), so a bio drawn outside it
// would lose its inset.

const HERO_MONTH_FORMATTER = new Intl.DateTimeFormat("en-US", { month: "long" });

// Two months is the shortest window that still crosses a month boundary no
// matter which day of the month it starts on, which is all the chip needs in
// order to name this month or the next one.
const HERO_AVAILABILITY_WINDOW_DAYS = 62;

// The weekly pattern is the only availability the public profile payload
// carries, so this answers "is the diary open at all", not "is 2pm on the 14th
// still free" — the slot picker's per-date call is the authority on that.
const findNextOpenDate = (availability) => {
  const cursor = new Date();
  cursor.setHours(0, 0, 0, 0);

  for (let offset = 0; offset < HERO_AVAILABILITY_WINDOW_DAYS; offset += 1) {
    const date = new Date(cursor);
    date.setDate(date.getDate() + offset);
    const day = getDayAvailabilityForDate(availability, toISODateString(date));
    if (Array.isArray(day?.slots) && day.slots.length > 0) {
      return date;
    }
  }

  return null;
};

const HeroTagIcon = ({ children }) => (
  <svg
    className="profile-hero__tag-icon"
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

// Three chips in a fixed order: whether the diary is open, where the work
// happens, and how a client is allowed to pay. Each is built only from fields
// the public profile endpoint already returns — the street address is stripped
// server-side, so city/state is all we can show.
export const buildHeroTags = ({ professional, availability, services, isCashOnly }) => {
  const nextOpenDate = findNextOpenDate(availability);
  const locationLabel = [professional?.location?.city, professional?.location?.state]
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter(Boolean)
    .join(", ");
  // A pro without Stripe can only ever be paid in person; one with Stripe
  // offers it alongside card only when the business setting is on.
  const acceptsInPersonPayment =
    isCashOnly ||
    (Array.isArray(services) &&
      services.some(
        (service) => service?.bookingSettings?.inPersonPaymentEnabled === true,
      ));

  return [
    {
      key: "availability",
      tone: nextOpenDate ? "positive" : "muted",
      icon: nextOpenDate ? <HeroTagIcon><path d="M20 6 9 17l-5-5" /></HeroTagIcon> : null,
      label: nextOpenDate
        ? `Bookings available for ${HERO_MONTH_FORMATTER.format(nextOpenDate)}`
        : "Not taking bookings right now",
    },
    locationLabel
      ? {
          key: "location",
          tone: "neutral",
          icon: (
            <HeroTagIcon>
              <path d="M20 10c0 5-5.5 10.2-7.4 11.8a1 1 0 0 1-1.2 0C9.5 20.2 4 15 4 10a8 8 0 0 1 16 0" />
              <circle cx="12" cy="10" r="3" />
            </HeroTagIcon>
          ),
          label: locationLabel,
        }
      : null,
    {
      key: "payment",
      tone: "neutral",
      icon: (
        <HeroTagIcon>
          <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
          <path d="M2.5 10h19" />
        </HeroTagIcon>
      ),
      label: acceptsInPersonPayment
        ? "Accepts in-person payment"
        : "Online payment only",
    },
  ].filter(Boolean);
};

const handleInlineSingleLineKeyDown = (event) => {
  if (event.key === "Enter") {
    event.preventDefault();
    event.currentTarget.blur();
  }
};

const ProfileHeaderSection = ({
  editorMode = false,
  pageTitle,
  heroSubtitle,
  heroTags = [],
  aboutCopyHtml = null,
  avatarUrl = "",
  avatarAlt = "",
  avatarInitial = "",
  // Custom pages with the "images" header: the pro's header images, already
  // normalised. Empty draws the usual header.
  headerImages = [],
  // Editor only. When set, the disc becomes the control that changes the
  // picture.
  onAvatarEditRequest,
  onInlineTextChange,
  onThemeTargetSelect,
  onProfileContentEditRequest,
}) => {
  const canEditAvatar = editorMode && typeof onAvatarEditRequest === "function";

  const handleInlineEditInteraction = (event) => {
    if (!editorMode) return;
    event.stopPropagation();
  };

  const handleAvatarEditRequest = (event) => {
    if (!canEditAvatar) return;
    event.preventDefault();
    event.stopPropagation();
    onAvatarEditRequest();
  };

  const handleAvatarKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    handleAvatarEditRequest(event);
  };

  const handleProfileContentEditRequest = (event) => {
    if (!editorMode) return;
    event.preventDefault();
    event.stopPropagation();
    onThemeTargetSelect?.("text");
    onProfileContentEditRequest?.();
  };

  const handleProfileContentKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleProfileContentEditRequest(event);
    }
  };

  const handleInlineTextFieldChange = (field, value) => {
    if (!editorMode) return;
    onInlineTextChange?.(field, value);
  };

  // "Stacked photos": the pro's own images, one under another, in place of
  // the picture, name and chips. The name still heads the page for screen
  // readers and search, and the bio still follows.
  if (headerImages.length > 0) {
    return (
      <section className="profile-hero profile-hero--images">
        <h1 className="profile-hero__sr">{pageTitle}</h1>
        {heroSubtitle && <p className="profile-hero__sr">{heroSubtitle}</p>}
        <div className="profile-hero__images">
          {headerImages.map((image, index) => (
            <img
              key={image.id}
              className="profile-hero__image"
              src={cloudinaryImageUrl(image.url, { width: IMAGE_WIDTH.gallery })}
              alt={image.alt}
              loading={index === 0 ? "eager" : "lazy"}
            />
          ))}
        </div>
        {aboutCopyHtml && (
          <div className="profile-bio" dangerouslySetInnerHTML={aboutCopyHtml} />
        )}
      </section>
    );
  }

  return (
    <section className="profile-hero">
      <div className="profile-hero__row">
        {/* In the editor the disc becomes the control that changes the
            picture, so the owner edits it where they can see it -- the
            same move the headings make with contentEditable. */}
        <div
          className={`profile-hero__avatar${canEditAvatar ? " is-editable" : ""}`}
          role={canEditAvatar ? "button" : undefined}
          tabIndex={canEditAvatar ? 0 : undefined}
          aria-label={
            canEditAvatar
              ? avatarUrl
                ? "Change profile picture"
                : "Add a profile picture"
              : undefined
          }
          onMouseDown={canEditAvatar ? handleInlineEditInteraction : undefined}
          onClick={canEditAvatar ? handleAvatarEditRequest : undefined}
          onKeyDown={canEditAvatar ? handleAvatarKeyDown : undefined}
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt={avatarAlt} />
          ) : (
            <span className="profile-hero__avatar-initial" aria-hidden="true">
              {avatarInitial}
            </span>
          )}
          {canEditAvatar && (
            <span className="profile-hero__avatar-edit" aria-hidden="true">
              {avatarUrl ? "Change photo" : "Add photo"}
            </span>
          )}
        </div>
        <div className="profile-hero__copy">
          <h1
            data-theme-target={editorMode ? "text" : undefined}
            contentEditable={editorMode}
            suppressContentEditableWarning={editorMode}
            onBlur={(event) =>
              handleInlineTextFieldChange(
                "businessName",
                event.currentTarget.textContent || ""
              )
            }
            onMouseDown={handleInlineEditInteraction}
            onClick={handleInlineEditInteraction}
            onKeyDown={handleInlineSingleLineKeyDown}
            role={editorMode ? "textbox" : undefined}
            aria-label={editorMode ? "Edit business name" : undefined}
          >
            {pageTitle}
          </h1>
          {(heroSubtitle || editorMode) && (
            <p
              className="profile-hero__subtitle"
              data-theme-target={editorMode ? "text" : undefined}
              data-inline-edit-field={editorMode ? "tagline" : undefined}
              contentEditable={editorMode}
              suppressContentEditableWarning={editorMode}
              onBlur={(event) =>
                handleInlineTextFieldChange(
                  "tagline",
                  event.currentTarget.textContent || ""
                )
              }
              onMouseDown={handleInlineEditInteraction}
              onClick={handleInlineEditInteraction}
              onKeyDown={handleInlineSingleLineKeyDown}
              role={editorMode ? "textbox" : undefined}
              aria-label={editorMode ? "Edit tagline" : undefined}
            >
              {heroSubtitle}
            </p>
          )}
          {heroTags.length > 0 && (
            <ul className="profile-hero__tags">
              {heroTags.map((tag) => (
                <li
                  className={`profile-hero__tag profile-hero__tag--${tag.tone}`}
                  key={tag.key}
                >
                  {tag.icon}
                  {tag.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {/* The design keeps the bio as a paragraph in the hero rather than a
          card of its own, so it reads as part of the introduction. */}
      {aboutCopyHtml && (
        <div
          className="profile-bio"
          data-theme-target={editorMode ? "text" : undefined}
          onClick={editorMode ? handleProfileContentEditRequest : undefined}
          onKeyDown={editorMode ? handleProfileContentKeyDown : undefined}
          role={editorMode ? "button" : undefined}
          tabIndex={editorMode ? 0 : undefined}
          aria-label={editorMode ? "Edit profile content" : undefined}
          dangerouslySetInnerHTML={aboutCopyHtml}
        />
      )}
    </section>
  );
};

export default ProfileHeaderSection;
